import React, { useState } from 'react';
import {
  Box,
  Button,
  Divider,
  FormControlLabel,
  FormGroup,
  Grid,
  IconButton,
  Modal,
  Paper,
  Stack,
  Switch,
  Tooltip,
  Typography,
} from '@mui/material';
import { Close } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { authApiClient } from '../../../utils/axios';
import { showSnackbar } from '../../../api/snackbarSlice';
import { logout } from '../../../api/auth/authSlice';
import useDocumentTitle from '../../../hooks/useDocumentTitle';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { updateOrgSettingsRequest } from '../../../api/organizations/orgSlice';

const AccountSection = () => {
  useDocumentTitle('Account Section');
  const user = useSelector((state) => state.org.org.user);
  const { settings } = useSelector((state) => state.org);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const handleClose = () => {
    setOpen(false);
  };

  const handleContinue = () => {
    setLoading(true);
    authApiClient
      .post('/generate-otp', user)
      .then((res) => {
        dispatch(logout());
        setTimeout(() => {
          navigate('/forgot-password', {
            state: { email: user.email, stage: 'verification' },
            replace: true,
          });
          dispatch(showSnackbar({ message: 'OTP sent successfully', severity: 'success' }));
        }, 0);
      })
      .catch((err) => {
        dispatch(
          showSnackbar({
            message: err?.response?.data?.errors?.message ?? 'Something went wrong.',
            severity: 'error',
          }),
        );
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <>
      <Paper elevation={0} sx={{ p: '1rem' }}>
        <Box sx={{ mb: '1.5rem' }}>
          <Typography variant="h6">Security</Typography>
          <Divider></Divider>
        </Box>
        <Grid container>
          <Grid size={12}>
            <Typography variant="body2" sx={{ mb: 2 }}>
              Change your current password that you use to login to this account.
            </Typography>
            <Button variant="contained" onClick={() => setOpen(true)}>
              Reset Password
            </Button>
          </Grid>
          {user?.is_admin && (
            <>
              <Grid size={12} sx={{ mt: 4 }}>
                <Typography variant="h6">Two-Factor Authentication</Typography>
                <Divider></Divider>
              </Grid>
              <Grid size={6} sx={{ mt: 2 }}>
                <FormGroup row>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={settings?.account_settings?.enable_2fa || false}
                        onChange={() => {
                          const updatedEnable2FA = !settings?.account_settings?.enable_2fa;
                          dispatch(
                            updateOrgSettingsRequest({
                              updates: { account_settings: { enable_2fa: updatedEnable2FA } },
                            }),
                          );
                          dispatch(
                            showSnackbar({
                              open: true,
                              message: `Two-Factor Authentication ${updatedEnable2FA ? 'enabled' : 'disabled'} successfully`,
                              severity: 'success',
                            }),
                          );
                        }}
                      />
                    }
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {'Enable Two-Factor Authentication'}
                        <Tooltip title="When enabled, additional security will be added to your account.">
                          <IconButton size="small">
                            <InfoOutlinedIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    }
                    labelPlacement="start"
                    sx={{ ml: 0, justifyContent: 'space-between', width: '100%' }}
                  />
                </FormGroup>
              </Grid>
            </>
          )}
        </Grid>
      </Paper>
      <Modal open={open} onClose={handleClose}>
        <Paper
          style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 500,
            padding: 16,
            boxShadow: 24,
            outline: 'none',
          }}
        >
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">Reset Password</Typography>
            <IconButton onClick={handleClose}>
              <Close />
            </IconButton>
          </Stack>
          <Divider />

          <Typography variant="body1" mt={2} mb={2}>
            You will be logged out & redirected to <b>Reset Password</b>
          </Typography>
          <Stack direction="row" sx={{ justifyContent: 'flex-end' }}>
            <Button variant="contained" onClick={handleContinue}>
              {loading ? 'Processing...' : 'Continue'}
            </Button>
          </Stack>
        </Paper>
      </Modal>
    </>
  );
};

export default AccountSection;
